/**
 * CouponsStrip — coupon coverflow shown on home page. Tapping a coupon
 * copies the code to the clipboard and pops a toast — no checkout
 * navigation required (the user is still browsing).
 *
 * Pulls coupons from the FIRST nearby store as a representative sample
 * (the full per-store list still appears on checkout). Falls back to a
 * tasteful "Sign up to unlock offers" card when no coupons exist.
 */
import React, { useEffect, useState } from 'react';
import { Tag, Copy, Check } from 'lucide-react';
import { SectionHeader, HorizontalRail, css, notify } from '../../design';
import { couponsAPI, Coupon } from '../../api/coupons';
import { Store } from '../../api/stores';

interface Props {
  stores: Store[];
}

const CouponsStrip: React.FC<Props> = ({ stores }) => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // Aggregate up to 3 stores' worth of coupons → dedupe by code.
        const all = await Promise.all(
          stores.slice(0, 3).map((s) =>
            couponsAPI.listAvailableCoupons(s.id).catch(() => [] as Coupon[]),
          ),
        );
        if (cancelled) return;
        const merged = ([] as Coupon[]).concat(...all);
        const dedup = new Map<string, Coupon>();
        for (const c of merged) if (!dedup.has(c.code)) dedup.set(c.code, c);
        setCoupons(Array.from(dedup.values()).slice(0, 8));
      } catch {
        if (!cancelled) setCoupons([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [stores]);

  if (loading) return null;
  if (coupons.length === 0) return null;

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      notify.success(`Coupon ${code} copied`, { duration: 1500 });
      setTimeout(() => setCopied((cur) => (cur === code ? null : cur)), 2000);
    } catch {
      notify.error('Could not copy code');
    }
  };

  return (
    <section style={{ marginBottom: 24 }}>
      <SectionHeader title="🎟️ Save more with coupons" subtitle="Tap to copy — apply at checkout" accent="accent" />
      <HorizontalRail ariaLabel="Available coupons" itemMinWidth={240}>
        {coupons.map((c) => {
          const valueLabel =
            c.discount_type === 'percentage'
              ? `${c.discount_value}% OFF${c.max_discount ? ` up to ₹${c.max_discount}` : ''}`
              : `₹${c.discount_value} OFF`;
          const isCopied = copied === c.code;
          return (
            <div
              key={c.id}
              style={{
                background: `linear-gradient(135deg, ${css.color.accentSoft}, ${css.color.primarySoft})`,
                border: `1px dashed ${css.color.accent}`,
                borderRadius: 14,
                padding: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minHeight: 92,
              }}
            >
              <div
                aria-hidden
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: css.color.accent,
                  color: '#FFFFFF',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Tag size={18} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: css.color.accentStrong, textTransform: 'uppercase' }}>
                  {valueLabel}
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: css.color.text, marginTop: 2 }}>
                  {c.code}
                </div>
                {c.min_order_amount > 0 && (
                  <div style={{ fontSize: 11, color: css.color.textMuted, marginTop: 2 }}>
                    Min order ₹{c.min_order_amount}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => copy(c.code)}
                aria-label={`Copy coupon ${c.code}`}
                style={{
                  background: '#FFFFFF',
                  border: `1px solid ${css.color.accent}`,
                  color: css.color.accentStrong,
                  borderRadius: 999,
                  padding: '6px 10px',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  flexShrink: 0,
                }}
              >
                {isCopied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
          );
        })}
      </HorizontalRail>
    </section>
  );
};

export default CouponsStrip;

